import React, { createContext, useMemo } from 'react';
import { MarketLocaleType } from '../types/administrationTypes';
import { RecordLocator } from '@tff/types';
import { TffBookingSource } from '@tff/types/tff';

interface Props {
  children: React.ReactNode;
  marketLocale: MarketLocaleType;
  currency: string | undefined;
  recordLocator?: RecordLocator;
  bookingSource?: TffBookingSource;
}

export const BookingContext = createContext({
  costFormatter: new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }),
  currency: 'EUR',
  numberFormatter: new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
  }),
  dateFormatter: new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' }),
  weekdayFormatter: new Intl.DateTimeFormat('de-DE', { weekday: 'short' }),
  timeFormatter: new Intl.DateTimeFormat('de-DE', { timeStyle: 'short' }),
  decimalSeparator: ',',
  allowedCharsRegex: new RegExp(`[^0-9,]`, 'g'),
  recordLocator: undefined as RecordLocator | undefined,
  bookingSource: undefined as TffBookingSource | undefined,
});

const BookingContextProvider = ({ marketLocale, children, currency = 'EUR', recordLocator, bookingSource }: Props) => {
  const decimalSeparator =
    marketLocale.startsWith('de') ||
    marketLocale.startsWith('fr') ||
    marketLocale.startsWith('es') ||
    marketLocale.startsWith('it')
      ? ','
      : '.';
  const allowedCharsRegex = new RegExp(`[^0-9${decimalSeparator.replace('.', '\\.')}]`, 'g');

  const value = useMemo(() => {
    const costFormatter = new Intl.NumberFormat(marketLocale, {
      style: 'currency',
      currency: currency,
    });

    const numberFormatter = new Intl.NumberFormat(marketLocale, {
      minimumFractionDigits: 2,
    });

    const dateFormatter = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' });
    const weekdayFormatter = new Intl.DateTimeFormat('de-DE', { weekday: 'short' });
    const timeFormatter = new Intl.DateTimeFormat('de-DE', { timeStyle: 'short' });

    return {
      costFormatter,
      currency,
      numberFormatter,
      dateFormatter,
      weekdayFormatter,
      timeFormatter,
      decimalSeparator,
      allowedCharsRegex,
      recordLocator,
      bookingSource,
    };
  }, [marketLocale, currency, decimalSeparator, allowedCharsRegex, recordLocator, bookingSource]);

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
};

export default BookingContextProvider;
